import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store.js';

// Socket.IO connects to the API server, not the Vite dev server
const SOCKET_URL = import.meta.env.DEV ? 'http://localhost:3000' : window.location.origin;

// Singleton socket — avoids spawning a fresh connection per useSocket() caller.
// Multiple consumers share one underlying connection; subscribers get the same
// `connected` state via subscribers Set.
let moduleSocket: Socket | null = null;
let moduleToken: string | null = null;
let moduleConnected = false;
const stateSubscribers = new Set<(connected: boolean) => void>();

function notifyState() {
  stateSubscribers.forEach((fn) => fn(moduleConnected));
}

function ensureSocket(token: string): Socket {
  if (moduleSocket && moduleToken === token) return moduleSocket;

  if (moduleSocket) {
    try {
      moduleSocket.disconnect();
    } catch {
      /* ignore */
    }
    moduleSocket = null;
  }

  moduleToken = token;
  const socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
  });

  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket.id);
    moduleConnected = true;
    notifyState();
  });
  socket.on('disconnect', () => {
    console.log('[Socket] Disconnected');
    moduleConnected = false;
    notifyState();
  });
  socket.on('connect_error', (err) => {
    console.error('[Socket] Connection error:', err.message);
  });

  moduleSocket = socket;
  return socket;
}

export function useSocket() {
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [connected, setConnected] = useState(moduleConnected);

  useEffect(() => {
    stateSubscribers.add(setConnected);
    return () => {
      stateSubscribers.delete(setConnected);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      if (moduleSocket) {
        try {
          moduleSocket.disconnect();
        } catch {
          /* ignore */
        }
        moduleSocket = null;
        moduleToken = null;
        moduleConnected = false;
        notifyState();
      }
      return;
    }
    ensureSocket(token);
    setConnected(moduleConnected);
  }, [token]);

  const emit = useCallback((event: string, ...args: any[]) => {
    moduleSocket?.emit(event, ...args);
  }, []);

  const on = useCallback((event: string, handler: (...args: any[]) => void) => {
    if (!moduleSocket) return () => {};
    moduleSocket.on(event, handler);
    return () => {
      moduleSocket?.off(event, handler);
    };
  }, []);

  return { socket: moduleSocket, connected, emit, on };
}
